import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
    ArrowRight,
    CheckCircle2,
    Play,
    Sparkles,
    Target,
    Award,
    LogOut,
    Lock,
} from "lucide-react";
import api from "../lib/api";
import WelcomeAudioButton from "../components/WelcomeAudioButton";
import { useAuth } from "../contexts/AuthContext";
import Logo from "../components/Logo";

const STEPS = [
    { id: "welcome", label: "Bienvenue" },
    { id: "video", label: "Vidéo" }, // Iter-27.44 : dynamiquement remplacé par "Présentation" si pas de vidéo
    { id: "module", label: "Pitch terrain" },
    { id: "complete", label: "Validation" },
];

export default function Onboarding() {
    const { refreshUser, user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [step, setStep] = useState(null); // null = unknown until first state check
    const [submitting, setSubmitting] = useState(false);
    const [settings, setSettings] = useState(null);
    const [moduleProgress, setModuleProgress] = useState(null);
    const stepKey = `shariti_onboarding_step_${user?.user_id || "anon"}`;
    const pollRef = useRef(null);

    // Load settings once
    useEffect(() => {
        api.get("/onboarding/settings").then((r) => setSettings(r.data));
    }, []);

    const requiredModuleId = settings?.required_module_id || "mod_pitch_terrain";

    const refreshModuleProgress = useCallback(async () => {
        try {
            const { data } = await api.get(`/modules/${requiredModuleId}`);
            setModuleProgress(data.progress);
            return data.progress;
        } catch {
            return null;
        }
    }, [requiredModuleId]);

    // First mount: derive the right step from actual user state.
    // Subsequent step changes: persist in localStorage so we restore on accidental refresh.
    useEffect(() => {
        if (!settings) return;
        let cancelled = false;
        (async () => {
            const progress = await refreshModuleProgress();
            if (cancelled) return;
            const completed = !!progress?.completed;
            const started = (progress?.progress_percent || 0) > 0;
            const stored = parseInt(localStorage.getItem(stepKey) || "", 10);
            let nextStep;
            if (completed) {
                // Module is done → land on the validation step
                nextStep = 3;
            } else if (started) {
                // User started the module → land back on the module step
                nextStep = 2;
            } else if (Number.isFinite(stored) && stored >= 0 && stored <= 3) {
                nextStep = stored;
            } else {
                nextStep = 0;
            }
            setStep(nextStep);
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings]);

    // Persist step changes to localStorage
    useEffect(() => {
        if (step !== null) {
            localStorage.setItem(stepKey, String(step));
        }
    }, [step, stepKey]);

    // While on the module step (and module not yet completed): poll progress every 3s and on tab focus.
    useEffect(() => {
        if (step !== 2 || moduleProgress?.completed) {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
            return;
        }
        pollRef.current = setInterval(refreshModuleProgress, 3000);
        const onFocus = () => refreshModuleProgress();
        window.addEventListener("focus", onFocus);
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = null;
            window.removeEventListener("focus", onFocus);
        };
    }, [step, moduleProgress?.completed, refreshModuleProgress]);

    // Auto-advance from step 2 to step 3 once the module gets completed during a poll
    useEffect(() => {
        if (step === 2 && moduleProgress?.completed) {
            setStep(3);
        }
    }, [moduleProgress?.completed, step]);

    const handleLogout = () => {
        logout(); // Iter-33 — logout gère lui-même le redirect (hard)
    };

    const moduleCompleted = !!moduleProgress?.completed;
    const moduleStarted = (moduleProgress?.progress_percent || 0) > 0;
    const allLessonsDone = (moduleProgress?.progress_percent || 0) >= 100;
    const quizPassed = (moduleProgress?.best_score ?? -1) >= 70;
    // "Quiz à passer" = leçons OK mais quiz pas encore validé
    const quizPending = allLessonsDone && !quizPassed;

    const finish = async () => {
        setSubmitting(true);
        try {
            await api.post("/onboarding/complete");
            toast.success("Onboarding validé ! +50 pts");
            window.dispatchEvent(new CustomEvent("shariti:points-changed"));
            localStorage.removeItem(stepKey);
            await refreshUser();
            navigate("/dashboard");
        } catch (err) {
            toast.error(
                err.response?.data?.detail || "Impossible de valider l'onboarding",
            );
        } finally {
            setSubmitting(false);
        }
    };

    const goToModule = () => {
        // Iter-30.31 — On force le mode Présentation (present=1) côté Module
        // pour que l'apprenant qui sort d'onboarding atterrisse sur l'expérience
        // immersive (slides + bouton Écouter), pas sur la vue classique
        // (très peu lisible sur mobile). Sans ce flag, le localStorage du
        // module pouvait imposer un "Classique" si l'apprenant avait switché
        // une fois auparavant.
        navigate(`/module/${requiredModuleId}?onboarding=1&present=1`);
    };

    const goToQuiz = () => {
        navigate(`/module/${requiredModuleId}?onboarding=1&tab=quiz&present=1`);
    };

    // Show loader until step is computed (avoids flash on Bienvenue when user actually completed everything)
    if (step === null || !settings) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-shariti-deep-black">
                <div className="w-10 h-10 rounded-full border-4 border-shariti-orange border-t-transparent animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-shariti-deep-black relative overflow-x-hidden">
            <div className="pointer-events-none fixed -top-40 -left-40 w-[400px] h-[400px] rounded-full bg-shariti-orange/15 blur-[100px] animate-blob" />
            <div className="pointer-events-none fixed bottom-0 right-0 w-[300px] h-[300px] rounded-full bg-shariti-yellow/10 blur-[100px] animate-blob" />

            {/* Top bar */}
            <header className="relative z-10 flex items-center justify-between px-4 sm:px-6 py-3">
                <Logo
                    variant="full"
                    showAcademy
                    academySize="sm"
                    className="h-10 sm:h-12 w-auto"
                />
                <button
                    onClick={handleLogout}
                    data-testid="onboarding-logout"
                    className="flex items-center gap-2 px-3 py-1.5 rounded-xl glass-card text-white/70 hover:text-shariti-orange hover:border-shariti-orange/40 transition-all text-sm"
                >
                    <LogOut className="w-4 h-4" />
                    <span className="font-bold">Quitter</span>
                </button>
            </header>

            {/* Step indicator */}
            <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 pt-2 pb-3">
                {/* Iter-27.44 — label adaptatif : "Vidéo" si vidéo configurée, "Présentation" sinon */}
                {(() => {
                    const hasVideo = !!settings?.welcome_video_url;
                    const dynamicSteps = STEPS.map((s) =>
                        s.id === "video" ? { ...s, label: hasVideo ? "Vidéo" : "Présentation" } : s
                    );
                    return (
                        <>
                            <div className="flex items-center gap-1.5">
                                {dynamicSteps.map((s, i) => (
                                    <div
                                        key={s.id}
                                        className={`flex-1 h-1 rounded-full transition-all ${
                                            i <= step ? "bg-shariti-orange" : "bg-white/5"
                                        }`}
                                    />
                                ))}
                            </div>
                            <div className="flex justify-between mt-1.5">
                                {dynamicSteps.map((s, i) => (
                                    <span
                                        key={s.id}
                                        className={`text-[9px] sm:text-[10px] font-bold uppercase tracking-widest ${
                                            i <= step ? "text-shariti-orange-light" : "text-white/30"
                                        }`}
                                    >
                                        {s.label}
                                    </span>
                                ))}
                            </div>
                        </>
                    );
                })()}
            </div>

            <main className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 pb-8">
                <AnimatePresence mode="wait">
                    {step === 0 && (
                        <motion.div
                            key="welcome"
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -16 }}
                            data-testid="onboarding-step-welcome"
                            className="text-center pt-1"
                        >
                            <div className="w-12 h-12 mx-auto rounded-2xl bg-shariti-orange/20 flex items-center justify-center mb-3">
                                <Sparkles className="w-6 h-6 text-shariti-orange" />
                            </div>
                            <p className="text-[10px] font-thin tracking-[0.4em] uppercase text-shariti-orange-light mb-1">
                                Bienvenue dans Shariti Academy
                            </p>
                            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight leading-tight mb-3">
                                Salut {user?.name?.split(" ")[0]}{" "}
                                <span className="inline-block">🔥</span>
                            </h1>
                            <p className="text-white/60 font-light text-sm max-w-lg mx-auto mb-5 text-balance">
                                Avant de te lancer, on prend 5 minutes ensemble pour poser les
                                bases. Un parcours rapide mais essentiel pour devenir redoutable
                                sur le terrain.
                            </p>
                            <button
                                onClick={() => setStep(1)}
                                data-testid="onboarding-start"
                                className="inline-flex items-center gap-2 bg-shariti-orange text-white font-extrabold py-3 px-6 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all"
                            >
                                Commencer le parcours
                                <ArrowRight className="w-4 h-4" />
                            </button>
                        </motion.div>
                    )}

                    {step === 1 && (
                        <motion.div
                            key="video"
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -16 }}
                            data-testid="onboarding-step-video"
                        >
                            <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light mb-1">
                                Étape 1
                            </p>
                            <h2 className="text-xl sm:text-2xl font-extrabold text-white tracking-tight mb-3">
                                {/* Iter-27.44 : titre adaptatif — pas "Vidéo de bienvenue" si pas de vidéo */}
                                {settings.welcome_video_url
                                    ? settings.welcome_title
                                    : (settings.welcome_title && settings.welcome_title.toLowerCase().includes("vidéo")
                                        ? "Bienvenue à bord"
                                        : settings.welcome_title)}
                            </h2>
                            {settings.welcome_video_url ? (
                                <div className="mb-3 mx-auto w-full">
                                    <div className="rounded-3xl overflow-hidden glass-card">
                                        <iframe
                                            src={settings.welcome_video_url}
                                            title="Bienvenue Shariti Academy"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                            className="w-full h-[34vh] sm:h-[40vh] block"
                                            data-testid="onboarding-video-iframe"
                                        />
                                    </div>
                                    {/* Iter-28 — Le message audio Shariti complète la vidéo : indispensable
                                        pour les apprenants ET les managers qui découvrent l'esprit de la maison */}
                                    <div className="mt-3 flex justify-center">
                                        <WelcomeAudioButton />
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {/* Iter-30.3 — Hero visuel avec hauteur fixe ET overflow-hidden.
                                        Le WelcomeAudioButton est désormais EN DESSOUS (et plus DANS)
                                        ce panel pour ne pas être clippé sur mobile (cas user qui voyait
                                        les boutons play/skip coupés en bas du panel). */}
                                    <div
                                        className="rounded-3xl overflow-hidden mb-4 mx-auto w-full relative"
                                        style={{
                                            height: "min(38vh, 300px)",
                                            background: "linear-gradient(135deg, rgba(238,114,46,0.62) 0%, rgba(20,20,20,0.94) 70%), url('https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1200&h=700&fit=crop&q=85') center/cover",
                                        }}
                                        data-testid="onboarding-welcome-fallback"
                                    >
                                        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-5 py-5">
                                            <motion.div
                                                initial={{ scale: 0.85, opacity: 0 }}
                                                animate={{ scale: 1, opacity: 1 }}
                                                transition={{ delay: 0.1, duration: 0.5 }}
                                                className="w-12 h-12 sm:w-14 sm:h-14 rounded-3xl bg-shariti-orange flex items-center justify-center mb-3 orange-glow"
                                            >
                                                <Sparkles className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                                            </motion.div>
                                            <p className="text-[10px] sm:text-[11px] font-thin tracking-[0.35em] uppercase text-shariti-orange-light mb-2">
                                                Bienvenue à bord
                                            </p>
                                            <p className="text-base sm:text-lg lg:text-xl text-white font-extrabold leading-snug max-w-xl">
                                                Tu rejoins une académie pensée pour devenir un recruteur d&apos;élite — terrain, humain, et fier de l&apos;impact que tu crées chaque jour.
                                            </p>
                                            <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
                                                <span className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-white bg-white/12 backdrop-blur px-2.5 py-1 rounded-full border border-white/15">
                                                    Pédagogie premium
                                                </span>
                                                <span className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-white bg-white/12 backdrop-blur px-2.5 py-1 rounded-full border border-white/15">
                                                    Mobile-first
                                                </span>
                                                <span className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-white bg-white/12 backdrop-blur px-2.5 py-1 rounded-full border border-white/15">
                                                    Récompenses réelles
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    {/* Iter-30.3 — Player audio SORTI du hero panel pour éviter le
                                        clipping bas sur mobile. Centré en dessous avec une marge propre. */}
                                    <div className="flex justify-center mb-3">
                                        <WelcomeAudioButton />
                                    </div>
                                </>
                            )}
                            {/* Iter-30 — line-clamp retiré : sur mobile le texte
                                "Découvre la mission..." se coupait avec "...". On laisse
                                respirer le texte sur toutes les tailles d'écran. */}
                            <p className="text-white/60 font-light text-xs sm:text-sm mb-4 leading-relaxed">
                                {settings.welcome_description}
                            </p>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setStep(0)}
                                    className="px-4 py-2.5 rounded-2xl glass-card text-white/70 hover:text-white text-sm font-bold"
                                >
                                    Retour
                                </button>
                                <button
                                    onClick={() => setStep(2)}
                                    data-testid="onboarding-video-next"
                                    className="flex-1 inline-flex items-center justify-center gap-2 bg-shariti-orange text-white font-extrabold py-2.5 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all"
                                >
                                    {settings.welcome_video_url ? "J'ai vu la vidéo" : "C'est parti"}
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {step === 2 && (
                        <motion.div
                            key="module"
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -16 }}
                            data-testid="onboarding-step-module"
                        >
                            <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light mb-1">
                                Étape 2 — obligatoire
                            </p>
                            <h2 className="text-xl sm:text-2xl font-extrabold text-white tracking-tight mb-3">
                                Module Pitch Terrain
                            </h2>
                            <div className="glass-card rounded-3xl p-4 sm:p-5 mb-3">
                                <div className="flex items-start gap-3 mb-3">
                                    <Target className="w-5 h-5 text-shariti-orange mt-0.5 shrink-0" />
                                    <div className="min-w-0">
                                        <h3 className="text-base sm:text-lg font-extrabold text-white mb-0.5">
                                            La base de tout recruteur
                                        </h3>
                                        <p className="text-xs sm:text-sm text-white/60 font-light">
                                            Visionne les leçons puis valide le quiz à 70%
                                            minimum. Ta progression se met à jour
                                            automatiquement ici.
                                        </p>
                                    </div>
                                </div>
                                <div className="bg-white/3 rounded-2xl p-3.5">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-[10px] font-thin uppercase tracking-widest text-white/50">
                                            Ta progression
                                        </span>
                                        {moduleCompleted ? (
                                            <span className="inline-flex items-center gap-1 text-[10px] font-extrabold text-shariti-orange uppercase tracking-widest">
                                                <CheckCircle2 className="w-3.5 h-3.5" />
                                                Validé
                                            </span>
                                        ) : quizPending ? (
                                            <span className="inline-flex items-center gap-1 text-[10px] font-extrabold text-shariti-orange-light uppercase tracking-widest animate-pulse">
                                                <Sparkles className="w-3.5 h-3.5" />
                                                Quiz à passer
                                            </span>
                                        ) : moduleStarted ? (
                                            <span className="text-[10px] font-bold text-shariti-yellow uppercase tracking-widest">
                                                En cours
                                            </span>
                                        ) : (
                                            <span className="text-[10px] font-bold text-white/40 uppercase tracking-widest">
                                                Non démarré
                                            </span>
                                        )}
                                    </div>
                                    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full rounded-full transition-all duration-500 ${
                                                moduleCompleted
                                                    ? "bg-shariti-orange"
                                                    : "bg-shariti-yellow"
                                            }`}
                                            style={{
                                                width: `${moduleProgress?.progress_percent || 0}%`,
                                            }}
                                        />
                                    </div>
                                    <div className="flex justify-between mt-1.5 text-[10px] sm:text-xs text-white/40">
                                        <span>
                                            {moduleProgress?.progress_percent || 0}% des leçons
                                        </span>
                                        {moduleProgress?.best_score != null && (
                                            <span>
                                                Quiz : {moduleProgress.best_score}%
                                                {moduleProgress.best_score >= 70 ? " ✓" : " (70% min)"}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-2">
                                <button
                                    onClick={() => setStep(1)}
                                    className="px-4 py-2.5 rounded-2xl glass-card text-white/70 hover:text-white text-sm font-bold"
                                >
                                    Retour
                                </button>
                                {moduleCompleted ? (
                                    <button
                                        onClick={() => setStep(3)}
                                        data-testid="onboarding-module-done"
                                        className="flex-1 inline-flex items-center justify-center gap-2 bg-shariti-orange text-white font-extrabold py-2.5 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all"
                                    >
                                        Module validé — suivant
                                        <ArrowRight className="w-4 h-4" />
                                    </button>
                                ) : quizPending ? (
                                    <button
                                        onClick={goToQuiz}
                                        data-testid="onboarding-go-to-quiz"
                                        className="flex-1 inline-flex items-center justify-center gap-2 bg-shariti-orange text-white font-extrabold py-2.5 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all"
                                    >
                                        <Sparkles className="w-4 h-4" />
                                        Passer le quiz maintenant
                                    </button>
                                ) : (
                                    <button
                                        onClick={goToModule}
                                        data-testid="onboarding-open-module"
                                        className="flex-1 inline-flex items-center justify-center gap-2 bg-shariti-orange text-white font-extrabold py-2.5 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all"
                                    >
                                        <Play className="w-4 h-4 fill-current" />
                                        {moduleStarted ? "Continuer le module" : "Ouvrir le module"}
                                    </button>
                                )}
                            </div>
                            {quizPending && (
                                <div
                                    className="mt-3 glass-card rounded-2xl p-3 sm:p-3.5 border border-shariti-orange/30 flex items-start gap-2.5"
                                    data-testid="onboarding-quiz-pending-banner"
                                >
                                    <Sparkles className="w-4 h-4 text-shariti-orange shrink-0 mt-0.5" />
                                    <p className="text-xs sm:text-[13px] text-white/80 leading-relaxed">
                                        <span className="font-extrabold text-white">Bravo, toutes les leçons sont vues 🎉.</span>{" "}
                                        Il ne reste plus que le quiz à valider (<span className="font-bold text-shariti-orange-light">70 % minimum</span>) pour débloquer la plateforme.
                                    </p>
                                </div>
                            )}
                            {!moduleCompleted && !quizPending && (
                                <p className="text-center text-[10px] text-white/30 mt-2">
                                    La progression se met à jour automatiquement (toutes les 3s)
                                </p>
                            )}
                        </motion.div>
                    )}

                    {step === 3 && (
                        <motion.div
                            key="complete"
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -16 }}
                            className="text-center pt-1"
                            data-testid="onboarding-step-complete"
                        >
                            {moduleCompleted ? (
                                <>
                                    <div className="w-12 h-12 mx-auto rounded-2xl bg-shariti-orange flex items-center justify-center mb-3 orange-glow">
                                        <CheckCircle2 className="w-7 h-7 text-white" />
                                    </div>
                                    <h2 className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-white tracking-tight mb-2">
                                        Tu es prêt·e à entrer dans la danse
                                    </h2>
                                    <p className="text-white/60 font-light text-xs sm:text-sm max-w-lg mx-auto mb-4">
                                        Toute la plateforme est maintenant débloquée. Simulateur
                                        IA, mode terrain, leaderboard, badges... à toi de jouer.
                                    </p>
                                    <div className="grid grid-cols-3 gap-2 max-w-sm mx-auto mb-4">
                                        {[
                                            { label: "Bonus", value: "+50 pts" },
                                            { label: "Badge", value: "Premier pas" },
                                            { label: "Niveau", value: "Recrue" },
                                        ].map((s) => (
                                            <div
                                                key={s.label}
                                                className="glass-card rounded-2xl p-2.5"
                                            >
                                                <p className="text-[9px] font-thin tracking-widest uppercase text-white/40 mb-0.5">
                                                    {s.label}
                                                </p>
                                                <p className="text-sm font-extrabold text-shariti-orange">
                                                    {s.value}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                    <button
                                        onClick={finish}
                                        disabled={submitting}
                                        data-testid="onboarding-finish"
                                        className="inline-flex items-center gap-2 bg-shariti-orange text-white font-extrabold py-3 px-6 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all disabled:opacity-60"
                                    >
                                        {submitting ? "..." : "Accéder à l'académie"}
                                        <Award className="w-5 h-5" />
                                    </button>
                                </>
                            ) : (
                                <>
                                    <div className="w-12 h-12 mx-auto rounded-2xl bg-white/5 flex items-center justify-center mb-3">
                                        <Lock className="w-6 h-6 text-white/40" />
                                    </div>
                                    <h2 className="text-xl sm:text-2xl font-extrabold text-white tracking-tight mb-2">
                                        Encore une étape
                                    </h2>
                                    <p className="text-white/60 font-light text-sm max-w-lg mx-auto mb-4">
                                        Le module Pitch Terrain n'est pas encore validé. Visionne
                                        les leçons puis réussis le quiz (70% minimum) pour
                                        débloquer la plateforme.
                                    </p>
                                    <button
                                        onClick={() => setStep(2)}
                                        className="inline-flex items-center gap-2 bg-shariti-orange text-white font-extrabold py-3 px-6 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all"
                                    >
                                        <ArrowRight className="w-4 h-4 rotate-180" />
                                        Reprendre le module
                                    </button>
                                </>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </main>
        </div>
    );
}
